'use client'
import React, { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Setting } from '@/types/theme'
import Pagination from '@/components/UiElements/Pagination'
import Modal from '@/components/Modal/Modal'
import Heading from '@/components/Heading/Heading'
import SearchBar from '@/components/UiElements/SearchBar'
import Splash from '@/components/splash/Splash'
import FontListItem from './FontListItem'
import FontForm from './FontForm'

import getSettings from '@/services/ThemeService/getSettings'
import updateSetting from '@/services/Settings/updateSetting'
import addSetting from '@/services/Settings/addSetting'
import mapToSave from '@/components/FontManager/mapData/mapToSave'
import useStateManagement from '@/hooks/stateManagement/useStateManagement'
import upload from '@/services/upload'


const perPage = 40

type FontManagerPageProps = {
	hideHeader?: boolean
}

export default function FontManagerPage({ hideHeader = false }: FontManagerPageProps) {

	const paginationRef = React.useRef<any>(null)

	const [isAddFontModalOpen, setIsAddFontModalOpen] = React.useState<boolean>(false)
	const [selectedFont, setSelectedFont] = React.useState<any>(null)
	const [loading, setLoading] = React.useState<boolean>(false)
	const [total, setTotal] = React.useState<number>(0)
	const [filteredFonts, setFilteredFonts] = React.useState<Array<any>>([])
	const [fonts, setFonts] = React.useState<Array<any>>([])
	const [filter, setFilter] = React.useState<{
		text: string,
		active: boolean,
		inActive: boolean,
	}>({
		text: '',
		active: true,
		inActive: true,
	})



	const handleAddNewFont = () => {
		setIsAddFontModalOpen(true);
	}



	const handleGetPageData = (pageNumber: number) => {
		const start = (pageNumber - 1) * perPage
		const end = start + perPage
		return filteredFonts.slice(start, end)
	}



	const handleCloseModal = () => {
		setIsAddFontModalOpen(false);
		setSelectedFont(null)
	}



	const handleLoadFonts = async () => {
		setLoading(true);
		const settingResult: Setting[] = await getSettings()
		if (!Array.isArray(settingResult)) {
			setLoading(false)
			return
		}

		const currentFontList:any[] = settingResult.find((item: Setting) => item.key === 'fontList')?.value;
		const fontList = currentFontList || [];

		setFilteredFonts(fontList);
		setFonts(fontList);
		setTotal(Math.ceil(fontList.length / perPage));
		setLoading(false);
	}


	const handleDeleteFont = async (fontId: string) => {

		const settingResult: Setting[] = await getSettings()

		const currentFontList:any[] = settingResult.find((item: Setting) => item.key === 'fontList')?.value;
		const fontList = currentFontList || [];
		const updatedList = fontList.filter((font:any) => font.id !== fontId);


		await updateSetting({value: updatedList, key: 'fontList'});
		setFonts(updatedList);
		setFilteredFonts(updatedList);

	}



	const handleClickEditFont = (font: any) => {
		setSelectedFont(font);
		setIsAddFontModalOpen(true);
	}



	const handleOnSearch = (text: string) => {
		setFilter({
			...filter,
			text: text
		});
	}



	const handleMoveToList = async (
		id: string,
		direction: "up" | "down"
	) => {
		const index = filteredFonts.findIndex(item => item.id === id);
		if (index === -1) return

		const target =
			direction === "up"
				? index - 1
				: index + 1;

		if (target < 0 || target >= filteredFonts.length) return ;

		const next = [...filteredFonts];
		[next[index], next[target]] = [next[target], next[index]];


		setFonts(next);
		setFilteredFonts(next);
		await updateSetting({value: next, key: 'fontList'});
	};


	const {
		runSteps: handleSaveFont,
	} = useStateManagement({
		loadCurrentList: {
			main: async (state, form) => {

				const settingResult: Setting[] = await getSettings()
				const currentFontList:any[] = settingResult.find((item: Setting) => item.key === 'fontList')?.value;
				return currentFontList;

			},
			startMessage: () => 'Loading current font list...',
			errorMessage: () => 'Failed to load current font list :('
		},
		data : {
			main: async (state, form) => {
				return {
					id: form.id && form.id !== "duplicate" ? form.id : uuidv4(),
					fontFamily: form.fontFamily,
					active: form.active,
					language: form.language,
					fontStyle: form.fontStyle,
					previewStyle: form.previewStyle
				};
			},
			startMessage: () => 'Preparing data',
		},

		uploadPreview: {
			main: async (state, form) => {
				const version =
					"_v_" +
					new Date()
					.toISOString()
					.slice(5, 23)
					.replace("T", "-")
					.replace(/:/g, "-")
					.replace(".", "-");
				const response:any = await upload({
					file: form.preview,
					fileName: `${form.fontFamily}-${state.data.id}-${version}-preview.png`,
					folderName: 'font-previews',
				})
				return(response);
			},
			startMessage: () => 'Uploading font preview...',
			errorMessage: () => 'Failed to upload font preview :('
		},

		uploadFonts: {
			main: async (state, form) => {
				const list:any[] = []
				for (const item of form.fontStyle) {
					const response = await upload({
						file: item.file.data,
						fileName: `${form.fontFamily}-${item.id}-${item.name}-${item.file.data.name}`,
						folderName: "fonts"
					});
					list[item.id] =  response;
				}
				return list
			},
			startMessage: () => 'Uploading font files...',
			errorMessage: () => 'Failed to upload font files :('

		},
		mapData: {
			main: async (state, form) => {
				console.log(form, state, mapToSave(state))
				return mapToSave(state);
			},
			startMessage: () => 'Mapping font data...',
			errorMessage: () => 'Failed to map font data :(',
		},
		updatedFontList: {
			main: async (state, form) => {
				let updatedList:any[] = []
				if (state.loadCurrentList) {
					updatedList = state.loadCurrentList.map((item:any) => {
							if (item.id === state.data.id) {
								return state.mapData
							} else {
								return item
							}
						}
					)
					// if not found, add new
					if (!updatedList.find((item:any) => item.id === state.data.id)) {
						updatedList.unshift(state.mapData)
					}
				} else {
					updatedList = [state.mapData]
				}
				return updatedList
			},
			startMessage: () => 'Updating font list...',
			errorMessage: () => 'Failed to update font list :(',
		},
		saveFont: {
			main: async (state, form) => {
				let res
				if (state.loadCurrentList) {
					res = await updateSetting({value: state.updatedFontList, key: 'fontList'});

				} else {
					res = await addSetting({value: state.updatedFontList, key: 'fontList'});
				}

				const responseMessage = typeof res === 'string' ? res : res?.message
				if (responseMessage !== 'updated' && responseMessage !== 'created') {
					throw new Error('Font save did not complete successfully')
				}

				setFonts(state.updatedFontList)
				setFilteredFonts(state.updatedFontList)
				handleCloseModal()
				await handleLoadFonts()
				return res

			},
			startMessage: () => 'Saving font...',
			errorMessage: () => 'Failed to save font :(',
			successMessage: () => 'Font saved successfully!'
		}

	})



	useEffect(() => {
		handleLoadFonts()
	}, [])



	useEffect(() => {
		const newList = fonts.filter((font) => {
			const string = `"${font.fontFamily.toLowerCase()}" "${font.fontStyle.map((s:any)=> s.name.toLowerCase()).join(' ')}"`;
			const text = filter.text.toLowerCase();
			return string.includes(text);
		}).filter((font) => {

			if (filter.active && font.active) return true;
			if (filter.inActive && !font.active) return true;
			return false;
		});
		setFilteredFonts(newList);
		setTotal(Math.ceil(newList.length/perPage));
	}, [filter, fonts]);


	useEffect(() => {
		paginationRef.current?.reset()
	}, [filteredFonts])





	return (
		<div className="flex w-full bg-white gap-md min-h-full">
			<Modal isOpen={isAddFontModalOpen} onClose={() => handleCloseModal()} title={selectedFont?.duplicatedFrom ? `Duplicate From "${selectedFont?.duplicatedFrom.fontFamily}"` : selectedFont?.id  ? "Edit Font" : "New Font"} closeOnOutsideClick={false}>
				<FontForm updateFont={selectedFont} saveFont={handleSaveFont}/>
			</Modal>
			<div className="flex-grow flex flex-col">


				{!hideHeader && (
					<Heading
						title={'Font Manager'}
						description={'Create and manage font families, styles, and organize fonts for use in text designs.'}
						childrenClassName={'!pb-sm'}
					/>
				)}
				<div className={'pr-xxl flex flex-col flex-grow'}>
					<div className={'min-h-[100%] relative'}>

						<div className={'sticky top-[48px] bg-white  z-[10]'}>
							<div className={'h-[48px] absolute top-[-48px]  w-full bg-white z-[-1]'}></div>
							<SearchBar
								placeholder={'Search by font name'}
								filters={[
									{
										label: 'Active',
										onClick: (value: boolean) => {
											setFilter(prevState => ({
												...prevState,
												active: value
											}))
										},
										count: fonts.filter((font) => font.active).length,
										default: filter.active
									},
									{
										label: 'In Active',
										onClick: (value: boolean) => {
											setFilter(prevState => ({
												...prevState,
												inActive: value
											}))
										},
										count: fonts.filter((font) => !font.active).length,
										default: filter.inActive
									},
								]}
								options={[
									{
										label: (
											<span className="flex flex-row gap-md">
						                        <span className="font-icon-solid text-primary">+</span>
						                        Add New Font
						                    </span>
										),
										onClick: handleAddNewFont,
									},
								]}
								onChange={handleOnSearch}
							/>
						</div>
						{loading &&
							<Splash />
						}
						{(filteredFonts.length > 0 && total > 0) ?
							<Pagination
								ref={paginationRef}
								theme={(data) => {

									return (
										<FontListItem font={data} onDelete={handleDeleteFont} callBack={handleClickEditFont} move={handleMoveToList}/>
									)
								}}
								total={total}
								offset={150}
								getPageData={handleGetPageData}
								className={'pt-xxl'}
							/>
							:
							<div className={'flex flex-row justify-center items-center px-mx py-xl text-gray-300  bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'}>
								<p>
									<span>No results match your current filters. Try </span>
									<span
										className={'text-gray-300 underline cursor-pointer hover:text-gray-900 transition-all'}
										onClick={() => setFilter({
											text: '',
											active: true,
											inActive: true,
										})}
									>
										resetting some filters
									</span>
									<span> to see more options.</span>
								</p>
							</div>
						}
					</div>
				</div>

			</div>
		</div>
	)
}
